// apps/vendor/src/pages/auth/Verify2FAPage.tsx
//
// Phase 1 Wave 9 Task 45 — Vendor 2FA verification page (mirror of merchant
// Verify2FAPage with vendor-specific copy).

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  AuthSplitLayout,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormLabel,
  FormMessage,
  useAuth,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';

import { useTranslation } from '../../lib/i18n/index.js';

const STORAGE_KEY = 'pw_2fa_challenge';

function readChallengeToken(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearChallengeToken(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // noop
  }
}

interface DigitBoxesProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  ariaInvalid?: boolean;
}

function DigitBoxes({
  value,
  onChange,
  disabled,
  ariaInvalid,
}: DigitBoxesProps): JSX.Element {
  const { t } = useTranslation('auth');
  const handleChange = (i: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      const arr = value.split('');
      arr[i] = '';
      onChange(arr.join('').slice(0, 6));
      return;
    }
    const arr = value.split('');
    const remaining = raw.split('').slice(0, 6 - i);
    for (let k = 0; k < remaining.length; k++) {
      arr[i + k] = remaining[k] ?? '';
    }
    const joined = arr.join('').slice(0, 6);
    onChange(joined);
    const nextIndex = Math.min(i + remaining.length, 5);
    const nextEl = document.getElementById(`otp-box-${nextIndex}`) as HTMLInputElement | null;
    nextEl?.focus();
  };

  const handleKey = (i: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[i] && i > 0) {
      const prev = document.getElementById(`otp-box-${i - 1}`) as HTMLInputElement | null;
      prev?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) {
      const prev = document.getElementById(`otp-box-${i - 1}`) as HTMLInputElement | null;
      prev?.focus();
    }
    if (e.key === 'ArrowRight' && i < 5) {
      const nxt = document.getElementById(`otp-box-${i + 1}`) as HTMLInputElement | null;
      nxt?.focus();
    }
  };

  return (
    <div className="flex justify-between gap-2" role="group" aria-label={t('verify2fa.codeGroupAria')}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          id={`otp-box-${i}`}
          data-testid={`otp-box-${i}`}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={value[i] ?? ''}
          onChange={handleChange(i)}
          onKeyDown={handleKey(i)}
          disabled={disabled}
          aria-invalid={ariaInvalid ? 'true' : 'false'}
          className="h-12 w-12 rounded-md border border-pw-border bg-pw-bg text-center text-lg font-semibold text-pw-text placeholder:text-pw-text-muted/70 focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20 disabled:opacity-50"
        />
      ))}
    </div>
  );
}

export default function Verify2FAPage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fetchMe = useAuth((s) => s.fetchMe);
  const { t } = useTranslation('auth');

  const methodRaw = (params.get('method') ?? 'totp').toLowerCase();
  const method: 'totp' | 'email' = methodRaw === 'email' ? 'email' : 'totp';

  const [code, setCode] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError(t('verify2fa.error.mustBe6Digits'));
      return;
    }
    const challengeToken = readChallengeToken();
    if (!challengeToken) {
      setError(t('verify2fa.error.sessionExpired'));
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const client = apiClient();
      const { response } = await (
        client as unknown as {
          POST: (
            path: string,
            init: { body: unknown },
          ) => Promise<{ response: Response }>;
        }
      ).POST('/api/v1/auth/verify-2fa', {
        body: {
          challengeToken,
          code,
          rememberDevice,
        },
      });
      if (!response.ok) {
        if (response.status === 410) {
          setError(t('verify2fa.error.codeExpired'));
        } else if (response.status === 423) {
          // UAT #2366 — attempt ceiling tripped (3 fails / 5 min → 15-min lock).
          setError(t('verify2fa.error.locked'));
        } else if (response.status === 429) {
          setError(t('verify2fa.error.rateLimit'));
        } else {
          setError(t('verify2fa.error.invalidCode'));
        }
        return;
      }
      clearChallengeToken();
      await fetchMe();
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'VERIFY_2FA_FAILED');
    } finally {
      setSubmitting(false);
    }
  };

  const headline =
    method === 'email'
      ? t('verify2fa.headlineEmail')
      : t('verify2fa.headlineTotp');

  return (
    <AuthSplitLayout variant="verification">
      <div className="flex flex-1 items-center justify-center">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('verify2fa.title')}</CardTitle>
          <CardDescription>{headline}</CardDescription>
        </CardHeader>
        <CardContent>
          <Form onSubmit={submit} noValidate>
            <FormField>
              <FormLabel>{t('verify2fa.labelCode')}</FormLabel>
              <DigitBoxes
                value={code}
                onChange={setCode}
                disabled={submitting}
                ariaInvalid={!!error}
              />
              {error ? <FormMessage>{error}</FormMessage> : null}
            </FormField>

            <label className="flex items-center gap-2 text-sm text-pw-text-muted">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-pw-border"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
              />
              {t('verify2fa.rememberDevice')}
            </label>

            <Button
              type="submit"
              variant="default"
              size="default"
              className="w-full"
              disabled={submitting}
              data-testid="verify-2fa-submit"
            >
              {submitting ? t('verify2fa.submitting') : t('verify2fa.submit')}
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="default"
              className="w-full"
              onClick={() => navigate('/login')}
            >
              {t('verify2fa.backToLogin')}
            </Button>
          </Form>
        </CardContent>
      </Card>
    </div>
    </AuthSplitLayout>
  );
}
